<template>
  <div class="manager-dashboard">
    <!-- Header avec nom de l'école -->
    <dashboard-context-header title="MON ÉCOLE" @context-change="onContextChange" />

    <!-- Cartes principales -->
    <div class="cards-grid">
      <!-- Aiguillage §3 (meal-feedback-restitution) : selon `show_detailed_feedback` de la
           cantine, on propose SOIT la saisie du retour détaillé par plat (SupervisorFeedback),
           SOIT « Mon avis sur le repas du jour » (MealFeedback) — jamais les deux à la fois.
           La cantine, le service et la date viennent du contexte du dashboard. -->
      <supervisor-feedback-panel
        v-if="featureDetailedFeedback"
        :canteen-id="canteenId"
        :service-id="selectedService"
        :date="selectedDate"
        @saved="reloadSupervisorStats"
      />
      <meal-feedback-panel
        v-else-if="featureMealFeedback"
        :canteen-id="canteenId"
        :service-id="selectedService"
        :date="selectedDate"
        @saved="reloadMealStats"
      />

      <!-- Restitution de l'agrégat de satisfaction repas (deux moyennes + écart).
           Rechargée sur l'évènement `saved` du formulaire, pour que la note apparaisse
           sans attendre un rechargement complet de la page. -->
      <meal-feedback-stats-panel ref="mealFeedbackStats" :canteen-id="canteenId" />
      <supervisor-feedback-stats-panel ref="supervisorFeedbackStats" :canteen-id="canteenId" />

      <!-- Card 1: Repas du jour -->
      <dashboard-card
        title="🍽️ Repas du jour"
        :description="mealCardDesc"
        badge="Aujourd'hui"
        link-label="Voir les détails"
        @click="showMealDetails = true"
      />

      <!-- Card 2: Communications -->
      <dashboard-card
        v-if="featureCommunications"
        title="📢 Communications"
        description="Absences, spécificités alimentaires et messages"
        :badge="unreadMessagesBadge"
        badge-variant="orange"
        link-label="Voir les messages"
        @click="showCommunications = true"
      />

      <!-- Card 3: Effectifs -->
      <dashboard-card
        v-if="featureEffectifs"
        title="👥 Effectifs"
        description="Configurer les effectifs prévus et spécificités alimentaires par niveau"
        badge="Par niveau"
        link-label="Modifier"
        @click="showEffectifs = true"
      />

      <!-- Card 3: Feedback -->
      <dashboard-card
        title="📝 Feedback quotidien"
        description="Retours superviseurs et votes enfants du jour"
        :badge="feedbackBadge"
        badge-variant="yellow"
        link-label="Voir"
        @click="showFeedbackStats = true"
      />

      <!-- Card 4: Surplus -->
      <dashboard-card
        v-if="featureSurplus"
        title="📦 Gestion surplus"
        description="Déclarer et valoriser les excédents alimentaires"
        :badge="surplusBadge"
        badge-variant="red"
        link-label="Gérer"
        @click="showSurplusSection = true"
      />

      <!-- Card 5: Statistiques -->
      <dashboard-card
        title="📊 Statistiques"
        description="Satisfaction et gaspillage de la semaine"
        badge="Cette semaine"
        badge-variant="green"
        link-label="Consulter"
        @click="showStats = true"
      />

      <!-- Card 6: Gaspillage. Le badge forme une paire opposée vert/rouge : une tendance à la
           baisse est favorable, une hausse ne l'est pas. Le libellé porte le sens, la couleur
           ne fait que le redoubler. -->
      <dashboard-card
        v-if="featureWaste"
        title="♻️ Gaspillage"
        description="Visualisation et sensibilisation au gaspillage"
        :badge="wasteBadge"
        :badge-variant="wasteBadgeVariant"
        link-label="Voir"
        @click="showWasteSection = true"
      />

      <!-- Card 7: Import menus -->
      <dashboard-card
        title="📥 Menus"
        description="Importer les menus de la semaine depuis un fichier"
        badge="Import"
        link-label="Importer"
        @click="showMenuImport = true"
      />

      <!-- Card 8: Services -->
      <dashboard-card
        title="⚙️ Services"
        description="Configurer les services de restauration et leurs niveaux"
        badge="Paramétrage"
        link-label="Configurer"
        @click="showServiceManagement = true"
      />

      <!-- Card 9: Gestion du personnel -->
      <dashboard-card
        v-if="canteenId"
        title="👩‍💼 Équipe"
        description="Gérer les membres de l'équipe et leurs accès"
        badge="Personnel"
        link-label="Gérer l'équipe"
        @click="goToTeamManagement"
      />

      <!-- Card : espace multi-cantines. Ce dashboard travaille sur *une* cantine ; la liste de
           toutes mes cantines, leur création et les outils transverses (import de diagnostics,
           actions en attente, territoire) vivent un cran au-dessus, sur `/mes-cantines`. -->
      <dashboard-card
        title="🏫 Mes cantines"
        description="Voir et créer mes cantines, importer des diagnostics"
        badge="Toutes"
        link-label="Ouvrir l'espace"
        @click="goToMyCanteens"
      />

      <!-- Card : plan d'actions Labricot (études alimentaires et défis auprès des convives).
           Anciennement un bandeau sur `/gestion` ; repris ici en card pour rester homogène avec
           la grille. Pas de condition sur la cantine sélectionnée : le plan d'actions est propre
           au manager, pas à une cantine. -->
      <dashboard-card
        title="🎯 Plan d'actions"
        description="Suivre les études alimentaires et les défis lancés auprès des convives"
        badge="Convives"
        badge-variant="green"
        link-label="Voir mon plan d'actions"
        @click="goToActionPlan"
      />

      <!-- Card : mise à jour des informations de la cantine sélectionnée.
           L'accès à CanteenEditor passe par ce dashboard, et non par l'espace multi-cantines.
           Navigation (et non modale) : formulaire long à plusieurs onglets (arbitrage §7.1).
           On ouvre l'onglet « informations » ; les autres onglets (satellites, équipe, affiche,
           publication, suppression) restent atteignables depuis la navigation interne de l'éditeur. -->
      <dashboard-card
        v-if="canteenId"
        title="🏫 Ma cantine"
        description="Mettre à jour les informations de la cantine et son affiche publique"
        badge="Informations"
        link-label="Modifier ma cantine"
        @click="goToCanteenEditor"
      />

      <!-- Cards réglementaires reprises du PoC « nouveau-dashboard-cantine » : bilan diagnostics,
           plan d'action nutrition et télédéclaration. Chacune est gouvernée par son propre flag de
           cantine (show_diagnostics / show_action_plan / show_teledeclaration) et navigue vers l'outil
           dédié. La destination `to` suffit à faire de la card un lien : le composant choisit son
           élément racine selon l'usage. -->
      <dashboard-card
        v-if="featureDiagnostics"
        :to="{ name: 'WasteDiagnosticResumePage' }"
        title="📋 Mes diagnostics"
        description="Consulter le bilan des diagnostics de la cantine"
        badge="Bilan"
        link-label="Voir mes diagnostics"
      />

      <dashboard-card
        v-if="featureActionPlan"
        :to="{ name: 'ManageResults' }"
        title="🥗 Mes actions nutrition"
        description="Suivre le plan d'action nutrition et ses résultats"
        badge="Nutrition"
        link-label="Voir mes actions"
      />

      <!-- `aria-busy` plutôt que `disabled` pendant l'ouverture : un bouton désactivé sort du
           parcours clavier et ne dit rien au lecteur d'écran (RGAA 7.1). La protection contre le
           double clic est déjà dans `openTeledeclaration`, qui sort si l'ouverture est en cours. -->
      <dashboard-card
        v-if="featureTeledeclaration && teledeclarationLink"
        title="📑 Mes télédéclarations"
        description="Remplir le bilan « gaspillage alimentaire » — vos pesées Labricot y sont proposées en préremplissage"
        :badge="`Déclaration ${teledeclarationYear}`"
        link-label="Ouvrir la télédéclaration"
        :aria-busy="teledeclarationBusy ? 'true' : 'false'"
        @click="openTeledeclaration"
      />

      <!-- Card 10: Export Excel (votes / déchets / effectifs) -->
      <dashboard-card
        :href="exportUrl"
        download
        title="📊 Export Excel"
        description="Télécharger votes, gaspillage et effectifs (fichier .xlsx)"
        badge="Données"
        badge-variant="green"
        link-label="Télécharger"
        aria-label="Télécharger l'export Excel des données de la cantine"
      />
    </div>

    <!-- Modal: Détails repas du jour -->
    <div v-if="showMealDetails" class="modal-overlay" @click="showMealDetails = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showMealDetails = false">✕</button>
        <h2 class="modal-title">🍽️ Repas du jour</h2>
        <p class="modal-subtitle">{{ mealSubtitle }}</p>

        <div class="detail-section">
          <h3 class="section-title">Effectifs</h3>
          <effectifs-panel
            :canteen-id="canteenId"
            :service-id="selectedService"
            :date="selectedDate"
            :readonly="true"
            :variant="effectifVariant"
          />
        </div>

        <div class="detail-section">
          <h3 class="section-title">Menu</h3>
          <menu-du-jour-panel :canteen-id="canteenId" :date="selectedDate" />
        </div>
      </div>
    </div>

    <!-- Modal: Communications -->
    <div v-if="showCommunications && featureCommunications" class="modal-overlay" @click="showCommunications = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showCommunications = false">✕</button>
        <communication-panel2
          :canteen-id="canteenId"
          :date="selectedDate"
          :variant="effectifVariant"
          :service-id="selectedService"
        />
      </div>
    </div>

    <!-- Modal: Effectifs -->
    <div v-if="showEffectifs && featureEffectifs" class="modal-overlay" @click="showEffectifs = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showEffectifs = false">✕</button>
        <h2 class="modal-title">👥 Effectifs</h2>
        <effectifs-panel
          :canteen-id="canteenId"
          :service-id="selectedService"
          :date="selectedDate"
          :readonly="false"
          :variant="effectifVariant"
        />
      </div>
    </div>

    <!-- Modal: Feedback Stats -->
    <div v-if="showFeedbackStats" class="modal-overlay" @click="showFeedbackStats = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showFeedbackStats = false">✕</button>
        <h2 class="modal-title">📝 Feedback du jour</h2>
        <p class="modal-subtitle">{{ selectedDate }}</p>

        <div v-if="feedbackStatsLoading" class="stats-row">
          <p style="text-align:center;color:#6b7280">Chargement…</p>
        </div>
        <div v-else-if="feedbackStats">
          <div class="stats-row">
            <div class="stat-item">
              <div class="stat-value">{{ feedbackStats.avgGlobal ? feedbackStats.avgGlobal.toFixed(1) : "—" }}</div>
              <div class="stat-label">Note moyenne /5</div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ feedbackStats.total || 0 }}</div>
              <div class="stat-label">Feedbacks reçus</div>
            </div>
          </div>
          <div
            v-if="feedbackStats.comments && feedbackStats.comments.length > 0"
            class="detail-section"
            style="margin-top:1rem"
          >
            <h3 class="section-title">Commentaires</h3>
            <div v-for="(comment, i) in feedbackStats.comments" :key="i" class="reservation-card">
              <p style="margin:0;font-size:0.875rem;font-weight:600;color:#374151">{{ comment.supervisor }}</p>
              <p style="margin:0.25rem 0 0;font-size:0.875rem;color:#374151">{{ comment.comment }}</p>
            </div>
          </div>
        </div>
        <p v-else style="text-align:center;color:#6b7280;padding:1rem">Aucun feedback pour aujourd'hui.</p>
      </div>
    </div>

    <!-- Modal: Surplus -->
    <div v-if="showSurplusSection" class="modal-overlay" @click="showSurplusSection = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showSurplusSection = false">✕</button>
        <h2 class="modal-title">📦 Gestion des surplus</h2>

        <div class="detail-section">
          <div v-if="surplusWeightKg > 0" class="alert alert-warning">
            <strong>⚠️ Alerte</strong>
            <p>{{ formattedSurplusWeight }}kg de surplus à valoriser aujourd'hui</p>
          </div>
          <div v-else class="alert alert-info">
            <strong>ℹ️ Information</strong>
            <p>Aucun surplus à valoriser aujourd'hui</p>
          </div>
        </div>

        <button
          @click="
            showSurplusForm = true
            showSurplusSection = false
          "
          class="btn btn-primary"
        >
          Déclarer un surplus
        </button>
        <button class="btn btn-secondary">Voir les associations disponibles</button>
      </div>
    </div>

    <!-- Modal: Surplus Form -->
    <div v-if="showSurplusForm" class="modal-overlay" @click="showSurplusForm = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showSurplusForm = false">✕</button>
        <surplus-form
          :canteen-id="canteenId"
          :service-id="selectedService"
          :meal-date="selectedDate"
          @close="
            showSurplusForm = false
            loadSurplusCount()
          "
        />
      </div>
    </div>

    <!-- Modal: Statistiques -->
    <div v-if="showStats" class="modal-overlay" @click="showStats = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showStats = false">✕</button>
        <h2 class="modal-title">📊 Statistiques de la semaine</h2>

        <div class="stats-row">
          <div class="stat-card stat-green">
            <div class="stat-icon">😊</div>
            <div class="stat-value">{{ weeklySatisfaction !== null ? weeklySatisfaction + "%" : "n/d" }}</div>
            <div class="stat-label">Satisfaction</div>
          </div>
          <div class="stat-card stat-blue">
            <div class="stat-icon">{{ weeklyWasteTrend !== null && weeklyWasteTrend <= 0 ? "📉" : "📈" }}</div>
            <div class="stat-value">{{ weeklyWasteTrendLabel }}</div>
            <div class="stat-label">Gaspillage</div>
          </div>
        </div>

        <div class="detail-section">
          <h3 class="section-title">Plats à surveiller</h3>
          <div v-if="weeklyWatchDishes.length > 0" class="ranking-list">
            <div v-for="(dish, idx) in weeklyWatchDishes" :key="dish.name" class="ranking-item">
              <span class="ranking-number">{{ idx + 1 }}</span>
              <span class="ranking-name">{{ dish.name }}</span>
              <span class="ranking-score">{{ dish.count }} signalement{{ dish.count > 1 ? "s" : "" }}</span>
            </div>
          </div>
          <p v-else class="empty-hint">
            {{ statsLoading ? "Chargement des statistiques…" : "Aucun plat signalé cette semaine." }}
          </p>
        </div>
      </div>
    </div>

    <!-- Modal: Gaspillage -->
    <div v-if="showWasteSection && featureWaste" class="modal-overlay" @click="showWasteSection = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showWasteSection = false">✕</button>
        <waste-input-panel
          :canteen-id="canteenId"
          :variant="wasteVariant"
          :meal-date="selectedDate"
          :service-id="selectedService"
        />
      </div>
    </div>

    <!-- Modal: Import menus -->
    <div v-if="showMenuImport" class="modal-overlay" @click="showMenuImport = false">
      <div class="modal-content" @click.stop>
        <button class="modal-close" @click="showMenuImport = false">✕</button>
        <h2 class="modal-title">📥 Importer les menus</h2>
        <p class="modal-subtitle">Fichier Markdown généré depuis le PDF de la cuisine centrale</p>
        <menu-import-panel :canteen-id="canteenId" @imported="onMenusImported" />
      </div>
    </div>

    <!-- Modal: Gestion des services -->
    <div v-if="showServiceManagement" class="modal-overlay" @click="showServiceManagement = false">
      <div class="modal-content modal-large" @click.stop>
        <button class="modal-close" @click="showServiceManagement = false">✕</button>
        <h2 class="modal-title">⚙️ Services de restauration</h2>
        <p class="modal-subtitle">Gérer les services et leurs niveaux (effectifs par type de repas)</p>
        <service-management-panel :canteen-id="canteenId" />
      </div>
    </div>
  </div>
</template>

<script>
import SurplusForm from "./SurplusForm.vue"
import CommunicationPanel2 from "@/components/CommunicationPanel2.vue"
import WasteInputPanel from "@/components/WasteInputPanel.vue"
import DashboardContextHeader from "@/components/DashboardContextHeader.vue"
import EffectifsPanel from "@/components/EffectifsPanel.vue"
import MenuDuJourPanel from "@/components/MenuDuJourPanel.vue"
import MenuImportPanel from "@/components/MenuImportPanel.vue"
import ServiceManagementPanel from "@/components/ServiceManagementPanel.vue"
import MealFeedbackPanel from "@/components/MealFeedbackPanel.vue"
import MealFeedbackStatsPanel from "@/components/MealFeedbackStatsPanel.vue"
import SupervisorFeedbackStatsPanel from "@/components/SupervisorFeedbackStatsPanel.vue"
import SupervisorFeedbackPanel from "@/components/SupervisorFeedbackPanel.vue"
import DashboardCard from "@/components/DashboardCard.vue"
import { diagnosticYears } from "@/utils"

export default {
  name: "ManagerDashboard",
  components: {
    SurplusForm,
    CommunicationPanel2,
    WasteInputPanel,
    DashboardContextHeader,
    EffectifsPanel,
    MenuDuJourPanel,
    MenuImportPanel,
    ServiceManagementPanel,
    MealFeedbackPanel,
    MealFeedbackStatsPanel,
    SupervisorFeedbackStatsPanel,
    SupervisorFeedbackPanel,
    DashboardCard,
  },
  data() {
    return {
      showMealDetails: false,
      showCommunications: false,
      showFeedbackStats: false,
      showSurplusSection: false,
      showSurplusForm: false,
      showStats: false,
      showWasteSection: false,
      showEffectifs: false,
      showMenuImport: false,
      showServiceManagement: false,
      unreadMessages: 0,
      hasSurplus: false,
      surplusCount: 0,
      surplusWeightKg: 0,
      feedbackStats: null,
      feedbackStatsLoading: false,
      canteenId: null,
      selectedDate: new Date().toISOString().split("T")[0],
      selectedService: null,
      effectifVariant: "SIMPLE",
      wasteVariant: "barquette",
      featureEffectifs: true,
      featureCommunications: true,
      featureWaste: true,
      featureSurplus: true,
      featureDetailedFeedback: true,
      featureMealFeedback: true,
      featureDiagnostics: true,
      featureActionPlan: true,
      featureTeledeclaration: true,
      teledeclarationBusy: false,
      totalPrevus: null,
      statsLoading: false,
      weeklySatisfaction: null,
      weeklyWasteTrend: null,
      weeklyWatchDishes: [],
    }
  },
  computed: {
    mealSubtitle() {
      // Date du jour sélectionnée, formatée en français. On reconstruit la date
      // à partir des composants (et non `new Date("YYYY-MM-DD")` qui parse en UTC
      // et peut décaler d'un jour selon le fuseau).
      let dateLabel = ""
      if (this.selectedDate) {
        const [y, m, d] = this.selectedDate.split("-").map(Number)
        const dt = new Date(y, m - 1, d)
        dateLabel = dt.toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })
        dateLabel = dateLabel.charAt(0).toUpperCase() + dateLabel.slice(1)
      }
      return dateLabel ? `${dateLabel} - Service du midi` : "Service du midi"
    },
    mealCardDesc() {
      if (!this.selectedService) return "Sélectionnez un service"
      if (this.totalPrevus === null) return "Chargement des effectifs…"
      return `${this.totalPrevus} élève${this.totalPrevus !== 1 ? "s" : ""} attendu${this.totalPrevus !== 1 ? "s" : ""}`
    },
    formattedSurplusWeight() {
      // Affichage compact : pas de décimale superflue (8 plutôt que 8.0).
      const kg = Math.round(this.surplusWeightKg * 10) / 10
      return Number.isInteger(kg) ? String(kg) : kg.toFixed(1)
    },
    weeklyWasteTrendLabel() {
      // Tendance gaspillage : signe explicite, « n/d » si non calculable.
      if (this.weeklyWasteTrend === null) return "n/d"
      return `${this.weeklyWasteTrend > 0 ? "+" : ""}${this.weeklyWasteTrend}%`
    },
    // Badges des cards. Le composant n'affiche rien pour une chaîne vide : l'absence de
    // badge se dit ici, elle ne se code plus par un `v-if` sur le `<span>`.
    unreadMessagesBadge() {
      return this.unreadMessages > 0 ? String(this.unreadMessages) : ""
    },
    feedbackBadge() {
      if (!this.feedbackStats) return "En attente"
      return this.feedbackStats.avgGlobal ? `${this.feedbackStats.avgGlobal.toFixed(1)}/5` : "Nouveau"
    },
    surplusBadge() {
      if (!this.surplusCount) return ""
      return `${this.surplusCount} offre${this.surplusCount > 1 ? "s" : ""}`
    },
    wasteBadge() {
      return this.weeklyWasteTrend === null ? "" : this.weeklyWasteTrendLabel
    },
    wasteBadgeVariant() {
      // Vert quand le gaspillage baisse ou stagne, rouge quand il monte. Le libellé
      // porte déjà le signe : la couleur ne fait que le redoubler.
      return this.weeklyWasteTrend !== null && this.weeklyWasteTrend <= 0 ? "green" : "red"
    },
    exportUrl() {
      // Export Excel agrégé (votes / déchets / effectifs), scoppé à la cantine
      // courante. Téléchargement direct via <a download> (auth par cookie de session).
      const base = "/api/v1/export-labricot.xlsx"
      return this.canteenId ? `${base}?canteen=${this.canteenId}` : base
    },
    teledeclarationYear() {
      // La campagne de télédéclaration porte sur l'année écoulée.
      return diagnosticYears()[0]
    },
    teledeclarationLink() {
      // Le tunnel de bilan amont s'adresse par `<id>--<nom>` : sans la cantine du
      // contexte, on ne sait pas construire le lien et la card ne s'affiche pas.
      const canteen = this.$store.state.userCanteenPreviews.find((c) => c.id === this.canteenId)
      if (!canteen) return null
      return {
        name: "DiagnosticTunnel",
        params: {
          canteenUrlComponent: this.$store.getters.getCanteenUrlComponent(canteen),
          year: this.teledeclarationYear,
          measureId: "gaspillage-alimentaire",
        },
      }
    },
  },
  methods: {
    // Recharge l'agrégat après une saisie, pour restituer la valeur sans reload de page.
    reloadSupervisorStats() {
      if (this.$refs.supervisorFeedbackStats) this.$refs.supervisorFeedbackStats.load()
    },
    reloadMealStats() {
      if (this.$refs.mealFeedbackStats) this.$refs.mealFeedbackStats.load()
    },
    openTeledeclaration() {
      // Le tunnel amont n'affiche rien tant que le diagnostic de l'année n'existe pas : il le
      // reçoit en prop depuis la cantine. On le crée donc au clic s'il manque, exactement comme
      // l'amont le fait avant d'entrer dans le tunnel (`MyProgress.startApproTunnel`).
      const link = this.teledeclarationLink
      if (!link || this.teledeclarationBusy) return
      this.teledeclarationBusy = true
      this.$store
        .dispatch("fetchCanteen", { id: this.canteenId })
        .then((canteen) => {
          const existing = (canteen.diagnostics || []).find((d) => d.year === this.teledeclarationYear)
          if (existing) return null
          return this.$store.dispatch("createDiagnostic", {
            canteenId: this.canteenId,
            payload: { year: this.teledeclarationYear, creationSource: "TUNNEL" },
          })
        })
        .then(() => this.$router.push(link))
        .catch((e) => this.$store.dispatch("notifyServerError", e))
        .finally(() => (this.teledeclarationBusy = false))
    },
    onContextChange({
      canteenId,
      selectedDate,
      selectedService,
      effectifVariant,
      wasteVariant,
      featureEffectifs,
      featureCommunications,
      featureWaste,
      featureSurplus,
      featureDetailedFeedback,
      featureMealFeedback,
      featureDiagnostics,
      featureActionPlan,
      featureTeledeclaration,
    }) {
      this.canteenId = canteenId
      this.selectedDate = selectedDate
      this.selectedService = selectedService
      this.effectifVariant = effectifVariant || "SIMPLE"
      this.wasteVariant = wasteVariant || "barquette"
      this.featureEffectifs = featureEffectifs ?? true
      this.featureCommunications = featureCommunications ?? true
      this.featureWaste = featureWaste ?? true
      this.featureSurplus = featureSurplus ?? true
      this.featureDetailedFeedback = featureDetailedFeedback ?? true
      this.featureMealFeedback = featureMealFeedback ?? true
      this.featureDiagnostics = featureDiagnostics ?? true
      this.featureActionPlan = featureActionPlan ?? true
      this.featureTeledeclaration = featureTeledeclaration ?? true
      if (canteenId && selectedDate) {
        this.loadFeedbackStats()
        this.loadSurplusCount()
        this.loadWeeklyStats()
      }
      if (selectedService) {
        this.loadEffectifSummary()
      }
    },

    async loadFeedbackStats() {
      if (!this.canteenId || !this.selectedDate) return
      this.feedbackStats = null
      this.feedbackStatsLoading = true
      try {
        const resp = await fetch(
          `/api/v1/canteen/${this.canteenId}/supervisorFeedbackStats/?date=${this.selectedDate}`,
          { credentials: "same-origin" }
        )
        if (resp.ok) {
          this.feedbackStats = await resp.json()
        }
      } catch {
        // silencieux
      } finally {
        this.feedbackStatsLoading = false
      }
    },

    async loadSurplusCount() {
      if (!this.canteenId || !this.selectedDate) return
      try {
        const resp = await fetch(
          `/api/v1/canteen/${this.canteenId}/surplusOffers/?date=${this.selectedDate}&is_available=true`,
          { credentials: "same-origin" }
        )
        if (resp.ok) {
          const data = await resp.json()
          const offers = Array.isArray(data) ? data : data.results || []
          this.surplusCount = Array.isArray(data) ? data.length : data.count || offers.length
          this.hasSurplus = this.surplusCount > 0
          // Poids total à valoriser = somme des quantités (kg) déclarées sur la
          // journée. `items` est un objet unique {dish_name, quantity_kg, ...}
          // (clés en snake_case, non camélisées car contenu d'un JSONField) ;
          // on tolère aussi un tableau d'items par prudence.
          const itemKg = (item) => Number(item && (item.quantity_kg ?? item.quantityKg)) || 0
          this.surplusWeightKg = offers.reduce((total, offer) => {
            const items = Array.isArray(offer.items) ? offer.items : offer.items ? [offer.items] : []
            return total + items.reduce((sum, item) => sum + itemKg(item), 0)
          }, 0)
        }
      } catch {
        // silencieux
      }
    },

    weekDates(refDateStr, weekOffset = 0) {
      // Renvoie les dates ISO (lundi → vendredi) de la semaine contenant refDate,
      // décalée de `weekOffset` semaines. Reconstruction par composants pour
      // éviter le décalage UTC de `new Date("YYYY-MM-DD")`.
      if (!refDateStr) return []
      const [y, m, d] = refDateStr.split("-").map(Number)
      const ref = new Date(y, m - 1, d)
      const dow = (ref.getDay() + 6) % 7 // 0 = lundi
      const monday = new Date(ref)
      monday.setDate(ref.getDate() - dow + weekOffset * 7)
      const days = []
      for (let i = 0; i < 5; i++) {
        const dt = new Date(monday)
        dt.setDate(monday.getDate() + i)
        const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(
          2,
          "0"
        )}`
        days.push(iso)
      }
      return days
    },

    async loadWeeklyStats() {
      if (!this.canteenId || !this.selectedDate) return
      this.statsLoading = true
      try {
        const currentWeek = this.weekDates(this.selectedDate, 0)
        const previousWeek = this.weekDates(this.selectedDate, -1)

        // --- Votes enfants de la semaine : satisfaction + plats à surveiller ---
        const voteResults = await Promise.all(
          currentWeek.map((date) =>
            fetch(`/api/v1/canteen/${this.canteenId}/childVotesStats/?date=${date}`, {
              credentials: "same-origin",
            })
              .then((r) => (r.ok ? r.json() : null))
              .catch(() => null)
          )
        )
        let liked = 0
        let totalVotes = 0
        const watchCounts = {}
        voteResults.filter(Boolean).forEach((stats) => {
          liked += stats.liked || 0
          totalVotes += stats.totalVotes || 0
          const problematic = stats.problematicDishesStats || {}
          Object.keys(problematic).forEach((dish) => {
            watchCounts[dish] = (watchCounts[dish] || 0) + problematic[dish]
          })
        })
        this.weeklySatisfaction = totalVotes > 0 ? Math.round((liked / totalVotes) * 100) : null
        this.weeklyWatchDishes = Object.keys(watchCounts)
          .map((name) => ({ name, count: watchCounts[name] }))
          .sort((a, b) => b.count - a.count)
          .slice(0, 3)

        // --- Gaspillage : tendance semaine courante vs semaine précédente ---
        const sumWeekWaste = async (week) => {
          const results = await Promise.all(
            week.map((date) =>
              fetch(`/api/v1/canteen/${this.canteenId}/wasteMeasurements/?date=${date}`, {
                credentials: "same-origin",
              })
                .then((r) => (r.ok ? r.json() : null))
                .catch(() => null)
            )
          )
          return results.filter(Boolean).reduce((total, payload) => {
            const list = Array.isArray(payload) ? payload : payload.results || []
            return total + list.reduce((sum, m) => sum + (Number(m.totalWeightKg) || 0), 0)
          }, 0)
        }
        const [currentWaste, previousWaste] = await Promise.all([sumWeekWaste(currentWeek), sumWeekWaste(previousWeek)])
        // Tendance non calculable sans référence (semaine précédente vide) → null.
        this.weeklyWasteTrend =
          previousWaste > 0 ? Math.round(((currentWaste - previousWaste) / previousWaste) * 100) : null
      } catch {
        // silencieux : le modal affiche alors les états « données insuffisantes »
      } finally {
        this.statsLoading = false
      }
    },

    onMenusImported(result) {
      this.showMenuImport = false
      this.$store.dispatch("notify", {
        message: `✅ ${result.total} plats importés (${result.created} nouveaux, ${result.updated} mis à jour)`,
        status: "success",
      })
    },

    goToTeamManagement() {
      this.$router.push(`/modifier-ma-cantine/${this.canteenId}/equipe/`)
    },

    goToMyCanteens() {
      this.$router.push({ name: "MyCanteensSpace" })
    },

    goToActionPlan() {
      this.$router.push({ name: "ActionPlan" })
    },

    goToCanteenEditor() {
      // Onglet « informations » de l'éditeur de cantine ; même construction d'URL que
      // l'accès équipe (identifiant brut comme composant d'URL).
      this.$router.push(`/modifier-ma-cantine/${this.canteenId}/modifier/`)
    },

    async loadEffectifSummary() {
      if (!this.selectedService) {
        this.totalPrevus = null
        return
      }
      try {
        const levels = await this.$store.dispatch("fetchServiceLevels", this.selectedService)
        this.totalPrevus = Array.isArray(levels)
          ? levels.reduce((s, l) => s + (l.nbStandardMeals ?? l.nb_standard_meals ?? 0), 0)
          : 0
      } catch {
        this.totalPrevus = null
      }
    },
  },
}
</script>

<style scoped>
/* Layout global */
.manager-dashboard {
  max-width: 1201px;
  margin: 0 auto;
  padding: 1.5rem;
  background-color: #f8f9fa;
  min-height: 100vh;
}

/* Header */
.dashboard-header {
  margin-bottom: 2rem;
  text-align: center;
}

.dashboard-title {
  font-size: 2rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
  letter-spacing: 0.5px;
}

.dashboard-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  margin: 0;
}

/* Grille de cartes */
.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.25rem;
}

@media (max-width: 768px) {
  .cards-grid {
    grid-template-columns: 1fr;
  }
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
  overflow-y: auto;
}

.modal-content {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.modal-large {
  max-width: 700px;
}

.modal-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #f3f4f6;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  color: #6b7280;
  transition: all 0.2s;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #1a1a1a;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 0.5rem 0;
}

.modal-subtitle {
  font-size: 0.875rem;
  color: #6b7280;
  margin: 0 0 1.5rem 0;
}

/* Sections de détails */
.detail-section {
  margin-bottom: 1.5rem;
}

.section-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 1rem 0;
}

/* Stats row */
.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 1rem;
}

.stat-item {
  text-align: center;
  background: #f9fafb;
  padding: 1rem;
  border-radius: 8px;
}

.stat-value {
  font-size: 1.875rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.25rem;
}

.stat-label {
  font-size: 0.875rem;
  color: #6b7280;
}

.stat-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 2px solid;
}

.stat-card.stat-green {
  border-color: #86efac;
  background: #f0fdf4;
}

.stat-card.stat-blue {
  border-color: #93c5fd;
  background: #eff6ff;
}

.stat-icon {
  font-size: 2.5rem;
  margin-bottom: 0.5rem;
}

/* Menu list */
.menu-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.menu-item {
  display: flex;
  justify-content: space-between;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.menu-label {
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
}

.menu-value {
  font-size: 0.875rem;
  color: #1a1a1a;
  font-weight: 600;
}

/* Ranking list */
.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem;
  background: #f9fafb;
  border-radius: 8px;
}

.ranking-number {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: #2563eb;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 0.875rem;
}

.ranking-name {
  flex: 1;
  font-weight: 500;
  color: #1a1a1a;
}

.ranking-score {
  font-weight: 700;
  color: #16a34a;
}

.empty-hint {
  color: #6b7280;
  font-style: italic;
  padding: 0.5rem 0;
}

/* Alerts */
.alert {
  padding: 1rem;
  border-radius: 8px;
  border-left: 4px solid;
}

.alert-warning {
  background: #fef3c7;
  border-color: #f59e0b;
}

.alert-info {
  background: #eff6ff;
  border-color: #3b82f6;
}

.alert strong {
  display: block;
  margin-bottom: 0.25rem;
  color: #92400e;
}

.alert p {
  margin: 0;
  font-size: 0.875rem;
  color: #78350f;
}

/* Buttons */
.btn {
  width: 100%;
  padding: 0.875rem 1.5rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 0.75rem;
}

.btn-primary {
  background: #2563eb;
  color: white;
}

.btn-primary:hover {
  background: #1d4ed8;
}

.btn-secondary {
  background: #f3f4f6;
  color: #1a1a1a;
}

.btn-secondary:hover {
  background: #e5e7eb;
}

/* Responsive */
@media (max-width: 640px) {
  .manager-dashboard {
    padding: 1rem;
  }

  .dashboard-title {
    font-size: 1.5rem;
  }

  .modal-content {
    padding: 1.5rem;
    border-radius: 12px;
  }
}
</style>
